import { Injectable } from '@angular/core'
import { v4 as uuid } from 'uuid';
import { getActionTypeFromInstance, getValue, InitState, setValue, StateToken, type NgxsNextPluginFn, type NgxsPlugin } from '@ngxs/store';
import { actionsToHandle } from '@medsurf/flat-decorators';
import * as FlatModels from '@medsurf/flat-models';
import { HISTORY_CONTROL_TOKEN, type HistoryControlStateModel } from '@medsurf/flat-states';
import * as Sentry from '@sentry/angular';

export interface StateDate {
  stateToken: StateToken<FlatModels.EntityStatesModels.EntityStateModel<FlatModels.Base | FlatModels.UserEntityModels.User>>;
  type: FlatModels.EntityControlModels.HistorySnapshotItemModifierType;
}

@Injectable()
export class NgxsHistoryPlugin implements NgxsPlugin {
  readonly #ACTIONS_TO_IGNORE = new Set([InitState.type]);
  
  handle(state: unknown,
         action: unknown,
         next: NgxsNextPluginFn) {
    const actionType = getActionTypeFromInstance(action);
    if (actionType) {
      const ignoreAction = this.#ACTIONS_TO_IGNORE.has(actionType);
      if (!ignoreAction) {
        state = this.#handleSnapshotItem(state, action, actionType);
      }
    }
    return next(state, action);
  }

  //<editor-fold desc="Snapshot Item">
  #handleSnapshotItem(state: unknown,
                      action: any,
                      actionType: string): unknown {
    // Get State Data
    const stateData = this.#getStateData(actionType);

    // Check if action is undoable
    if (!stateData) {
      return state;
    }

    // Get StateToken
    const stateToken = structuredClone(stateData.stateToken);

    // Check if action has a state token
    if (!stateToken) {
      return state
    }

    // Check if entity has an id (no entity can have an empty id)
    if (!action.entity.id) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        "notification.entity_has_no_id",
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatPlugins.HistoryPlugin.#handleSnapshotItem"
      );
    }
    
    // Check entity permission
    /* TODO remove this or should we display an error here (api call for permission)
    if (action.entity.id in allow) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        "notification.no_permission",
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatPlugins.HistoryPlugin.#handleSnapshotItem"
      );
    }
    */

    // Get entity state
    const entityState = this.#getState(state, (stateToken as any).name);

    // Get Snapshots
    const snapshots = structuredClone(entityState.snapshots);

    // Create previous snapshot of entity if not HistorySnapshotItemModifierType.enum.ADD
    let previousSnapshotId: string | null = null;
    if (stateData.type !== FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD) {
      // Get current entity
      const entity = this.#getEntity(entityState, action.entity.id);
      
      // Check for empty parentId in node
      if ((stateToken as any).name === FlatModels.EntityControlModels.EntityQueryType.enum.NODE_ENTITY) {
        if (entity.parentId !== null && action.entity.parentId === null) {
          try {
            throw new FlatModels.ErrorControlModels.MedsurfError(
              "notification.parent_node_empty",
              FlatModels.ErrorControlModels.ErrorLevel.enum.error,
              FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
              "LIBS.FlatPlugins.HistoryPlugin.#handleSnapshotItem",
              {},
              [{ cur: entity, new: action.entity }]
            );
          } catch (e) {
            Sentry.setContext('args', { cur: JSON.stringify(entity), new: JSON.stringify(action.entity) });
            Sentry.captureException(e);
            throw e;
          }
        }
      }

      // Check for duplicate entries of current entity
      const entityJson = JSON.stringify(entity);
      for (const key in snapshots) {
        // Return id when snapshot already exists
        if (snapshots[key].itemJson === entityJson) {
          previousSnapshotId = key;
          break;
        }
      }
      // Create new snapshot when current entity not already saved
      if (previousSnapshotId === null) {
        previousSnapshotId = uuid();
        snapshots[previousSnapshotId] = {
          itemJson: entityJson
        };
        state = setValue(state, (stateToken as any).name + '.snapshots', snapshots);
      }
    }

    // Create following snapshot of entity if not HistorySnapshotItemModifierType.enum.REMOVE
    let followingSnapshotId: string | null = null;
    if (stateData.type !== FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE) {
      // Check for duplicate entries
      const entityJson = JSON.stringify(action.entity);
      for (const key in snapshots) {
        // Return id when snapshot already exists
        if (snapshots[key].itemJson === entityJson) {
          followingSnapshotId = key;
          break;
        }
      }
      // Create new snapshot when entity not already exists
      if (followingSnapshotId === null) {
        followingSnapshotId = uuid();
        snapshots[followingSnapshotId] = {
          itemJson: entityJson
        };
        state = setValue(state, (stateToken as any).name + '.snapshots', snapshots);
      }
    }

    // Get User
    const user = getValue(state, 'authControl.user');
    if (!user) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        "notification.user_not_found",
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatPlugins.HistoryPlugin.#handleSnapshotItem"
      );
    }

    // Get snapshot item
    const snapshotItem = this.#getSnapshotItem(stateToken, stateData.type, action.entity.id, previousSnapshotId, followingSnapshotId, user);

    // Get history
    const _historyState = getValue(state, HISTORY_CONTROL_TOKEN.getName()) as HistoryControlStateModel;

    // Add snapshot to history state
    state = setValue(state, 'historyControl.currentChanges.snapshotItems', [..._historyState.currentChanges.snapshotItems, snapshotItem]);

    // Clean up future snapshots in entities
    if (_historyState.future.length > 0) {
      // Get All State Tokens And Clean Up Snapshots
      const cleanUpStateTokens: Set<string> = new Set<string>();
      const cleanUpSnapshots: Map<string, Set<string>> = [...getValue(state, 'historyControl.future')].reduce((arr: Map<string, Set<string>>, cleanUpSnapshot: FlatModels.EntityControlModels.HistorySnapshot) => {
        // Iterate Through Snapshot Items
        cleanUpSnapshot.snapshotItems
          .forEach((cleanUpSnapshotItem) => {
          // Get State Token
          const stateToken = (cleanUpSnapshotItem.stateToken as any).name;

          // Add Clean Up State Token
          cleanUpStateTokens.add(stateToken);

          // Get State Token Clean Up Snapshot Ids
          const stateTokenCleanUpSnapshotIds = arr.get(stateToken) || new Set<string>();

          // Add Snapshot Ids
          if (cleanUpSnapshotItem.previousSnapshotId) {
            stateTokenCleanUpSnapshotIds.add(cleanUpSnapshotItem.previousSnapshotId);
          }
          if (cleanUpSnapshotItem.followingSnapshotId) {
            stateTokenCleanUpSnapshotIds.add(cleanUpSnapshotItem.followingSnapshotId);
          }

          // Add To Array
          arr.set(stateToken, stateTokenCleanUpSnapshotIds);
        });

        // Return Map
        return arr;
      }, new Map<string, Set<string>>());

      // Get Current Snapshots
      const currentSnapshots = [
        ...getValue(state, 'historyControl.past'),
        getValue(state, 'historyControl.currentChanges'),
        ...getValue(state, 'historyControl.deleted')
      ].reduce((arr: Map<string, Set<string>>, snapshot: FlatModels.EntityControlModels.HistorySnapshot) => {
        // Iterate Through Snapshot Items
        snapshot.snapshotItems
          .filter(snapshotItem => Array.from(cleanUpStateTokens).includes((snapshotItem.stateToken as any).name))
          .forEach(snapshotItem => {
            // Get State Token
            const stateToken = (snapshotItem.stateToken as any).name;

            // Get State Token Snapshot Ids
            const stateTokenSnapshotIds = arr.get(stateToken) || new Set<string>();

            // Add Snapshot Ids
            if (snapshotItem.previousSnapshotId) {
              stateTokenSnapshotIds.add(snapshotItem.previousSnapshotId);
            }
            if (snapshotItem.followingSnapshotId) {
              stateTokenSnapshotIds.add(snapshotItem.followingSnapshotId);
            }

            // Add To Array
            arr.set(stateToken, stateTokenSnapshotIds);
          });

        // Return Map
        return arr;
      }, new Map<string, Set<string>>());

      // Remove Old Snapshots
      cleanUpSnapshots.forEach((cleanUpSnapshotIds, key) => {
        const currentSnapshotIds = currentSnapshots.get(key);
        if (currentSnapshotIds) {
          currentSnapshotIds.forEach(Set.prototype.delete, cleanUpSnapshotIds);
        }

        // Get Clean Up Entity State
        const cleanUpEntityState = this.#getState(state, key);

        // Get Clean Up Snapshots
        const cleanUpSnapshots = structuredClone(cleanUpEntityState.snapshots);

        // Clean Up Snapshots
        for (const cleanUpSnapshotId of cleanUpSnapshotIds) {
          // Remove snapshot
          delete cleanUpSnapshots[cleanUpSnapshotId];
        }

        // Save changes
        state = setValue(state, key + '.snapshots', cleanUpSnapshots);
      })

      // Clear future snapshots
      state = setValue(state, 'historyControl.future', []);
    }

    return state;
  }
  
  #getStateData(actionType: string): StateDate | null {
    const currentActionMetadata = Object.values(actionsToHandle).find((it) => it.actions.includes(actionType));

    if (!currentActionMetadata) {
      return null;
    }

    const index = currentActionMetadata.actions.indexOf(actionType);
    const type = currentActionMetadata.types[index];
    const stateToken = currentActionMetadata.stateTokens[index];

    return { stateToken, type};
  }
  
  #getState(state: any,
            stateToken: string): FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<any> {
    const _state = state[stateToken] as FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<any>;

    if (!_state) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        "notification.not_a_history_entity",
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatPlugins.HistoryPlugin.#getState"
      );
    }
    if (!_state.entities) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        "notification.no_entities_in_entity",
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatPlugins.HistoryPlugin.#getState"
      );
    }

    return _state;
  }
  
  #getSnapshotItem(stateToken: StateToken<FlatModels.EntityStatesModels.EntityStateModel<FlatModels.Base | FlatModels.UserEntityModels.User>>,
                   type: FlatModels.EntityControlModels.HistorySnapshotItemModifierType,
                   entityId: string,
                   previousSnapshotId: string | null,
                   followingSnapshotId: string | null,
                   user: FlatModels.UserEntityModels.User): FlatModels.EntityControlModels.HistorySnapshotItem {
    // Check if all snapshot id are provided
    switch (type) {
      case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD:
        if (followingSnapshotId === null) {
          throw new FlatModels.ErrorControlModels.MedsurfError(
            "notification.no_following_snapshot_found",
            FlatModels.ErrorControlModels.ErrorLevel.enum.critical,
            FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
            "LIBS.FlatPlugins.HistoryPlugin.#getSnapshotItem"
          );
        }
        break;
      case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE:
        if (previousSnapshotId === null) {
          throw new FlatModels.ErrorControlModels.MedsurfError(
            "notification.no_previous_snapshot_found",
            FlatModels.ErrorControlModels.ErrorLevel.enum.critical,
            FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
            "LIBS.FlatPlugins.HistoryPlugin.#getSnapshotItem"
          );
        }
        if (followingSnapshotId === null) {
          throw new FlatModels.ErrorControlModels.MedsurfError(
            "notification.no_following_snapshot_found",
            FlatModels.ErrorControlModels.ErrorLevel.enum.critical,
            FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
            "LIBS.FlatPlugins.HistoryPlugin.#getSnapshotItem"
          );
        }
        break;
      case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE:
        if (previousSnapshotId === null) {
          throw new FlatModels.ErrorControlModels.MedsurfError(
            "notification.no_previous_snapshot_found",
            FlatModels.ErrorControlModels.ErrorLevel.enum.critical,
            FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
            "LIBS.FlatPlugins.HistoryPlugin.#getSnapshotItem"
          );
        }
        break;
      default:
        throw new FlatModels.ErrorControlModels.MedsurfError(
          "notification.type_not_supported",
          FlatModels.ErrorControlModels.ErrorLevel.enum.critical,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatPlugins.HistoryPlugin.#getSnapshotItem"
        );
    }

    // Return new history snapshot item
    return new FlatModels.EntityControlModels.HistorySnapshotItem(stateToken, type, entityId, previousSnapshotId, followingSnapshotId, user)
  }

  #getEntity(state: any,
             entityId: string) {
    const entity = state.entities[entityId];

    if (!entity) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        "notification.no_entity_found",
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatPlugins.HistoryPlugin.#getEntity"
      );
    }

    return entity;
  }

  //</editor-fold>
}